<template>
  <vue-final-modal
    :value="visible"
    name="all-preview-dialog"
    :classes="['all-preview-dialog__container']"
    :styles="{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }"
    :content-class="['all-preview-dialog__content', 'v--modal-box', 'v--modal']"
    :click-to-close="true"
    :esc-to-close="true"
    :lock-scroll="true"
    attach="body"
    @input="onVisibleInput"
  >
    <div class="v--modal-close-top">
      <el-button
        @click="closeAllPreviewDialog"
        icon="el-icon-close"
        type="text"
      ></el-button>
    </div>
    <div class="backup-preview-dialog">
      <div class="formats-info">
        <div class="title">
          <p>プレビュー</p>
        </div>
      </div>
      <el-tabs
        v-model="activePreviewTab"
        class="backup-tabs"
        :stretch="true"
        @tab-click="onTabClick"
      >
        <el-tab-pane
          label="送付先情報を入力"
          name="basic_send_data"
          :lazy="true"
          :style="backupDataViewerCssRirekisho"
        >
          入力フォーム
        </el-tab-pane>
        <el-tab-pane
          label="履歴書"
          name="format_rirekisho"
          :lazy="true"
          :style="backupDataViewerCssRirekisho"
        >
          <rirekisho-preview
            ref="rirekishoPreview"
            :id-prefix="'backup'"
            :format-data="formatData.format_rirekisho"
            :basic-data="formatData.basicData"
            :preview-scale="previewScale"
          ></rirekisho-preview>
        </el-tab-pane>
        <el-tab-pane
          label="職務経歴書"
          name="format_shokumukeirekisho"
          :lazy="true"
          :style="backupDataViewerCssShokurekisho"
        >
          <shokurekisho-preview
            ref="shokurekishoPreview"
            :id-prefix="'backup'"
            :format-data="formatData.format_shokumukeirekisho"
            :basic-data="formatData.basicData"
            :preview-scale="previewScale"
          ></shokurekisho-preview>
        </el-tab-pane>
        <el-tab-pane
          label="送付状"
          name="format_letter"
          :lazy="true"
          :style="backupDataViewerCssRirekisho"
        >
          <letter-preview
            ref="letterPreview"
            :id-prefix="'backup'"
            :format-data="formatData.format_letter"
            :basic-data="formatData.basicData"
            :preview-scale="previewScale"
          ></letter-preview>
        </el-tab-pane>
      </el-tabs>
    </div>

    <div class="text-right">
      <el-button type="primary" @click="closeAllPreviewDialog"
        >決済情報を入力する</el-button
      >
      <el-button @click="closeAllPreviewDialog">閉じる</el-button>
    </div>
    <br /><br />
  </vue-final-modal>
</template>

<script setup lang="ts">
/**
 * 書式プレビューダイアログ（vue-final-modal）。
 * open は mitt（open-all-preview-dialog）。戻るは window popstate（VueUse）。
 * vue-js-modal / event bus は使わない。IndexedDB は localforage 明示 import。
 */
import localforage from 'localforage'
import {
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  reactive,
  ref,
  watch
} from 'vue'
import { useEventListener } from '@vueuse/core'
import { emitter } from '@/plugins/emitter'

const OPEN_ALL_PREVIEW_DIALOG = 'open-all-preview-dialog'
const DOC_PAPER_HEIGHT_PX = 794

type CssSize = Record<string, string>
type FormatData = Record<string, unknown>
type PreviewVm = {
  $el?: HTMLElement
  $refs?: { previewMain?: HTMLElement }
}
const visible = ref(false)
const formatData = reactive<FormatData>({})
const backupDataViewerCssRirekisho = ref<CssSize>({})
const backupDataViewerCssShokurekisho = ref<CssSize>({})
const previewScale = ref(1)
const activePreviewTab = ref('basic_send_data')
const rirekishoPreview = ref<PreviewVm | null>(null)
const shokurekishoPreview = ref<PreviewVm | null>(null)
const letterPreview = ref<PreviewVm | null>(null)

function onVisibleInput(value: boolean) {
  visible.value = value
}

function closeAllPreviewDialog() {
  visible.value = false
}

function activeViewer(): PreviewVm | null {
  if (activePreviewTab.value === 'format_rirekisho') {
    return rirekishoPreview.value
  }
  if (activePreviewTab.value === 'format_shokumukeirekisho') {
    return shokurekishoPreview.value
  }
  if (activePreviewTab.value === 'format_letter') {
    return letterPreview.value
  }
  return null
}

function setPreviewHeight() {
  const height =
    activeViewer()?.$refs?.previewMain?.getBoundingClientRect().height
  if (!height) return
  if (activePreviewTab.value === 'format_rirekisho' && height > 0) {
    backupDataViewerCssRirekisho.value = {
      height: height + 30 + 'px'
    }
  } else if (
    activePreviewTab.value === 'format_shokumukeirekisho' &&
    height > 0
  ) {
    backupDataViewerCssShokurekisho.value = {
      height: height + 30 + 'px'
    }
  } else if (activePreviewTab.value === 'format_letter' && height > 0) {
    backupDataViewerCssShokurekisho.value = {
      height: height + 30 + 'px'
    }
  }
}

function setPreviewScale() {
  nextTick(() => {
    const el = activeViewer()?.$el
    if (el && el.clientWidth !== 0) {
      const clientWidth = el.clientWidth
      previewScale.value =
        clientWidth / Math.max(clientWidth, DOC_PAPER_HEIGHT_PX)
      console.log(clientWidth, previewScale.value)
    }
    nextTick(() => {
      setPreviewHeight()
    })
  })
}

function onTabClick() {
  setPreviewScale()
}

function loadFormatData() {
  localforage
    .iterate((value, key) => {
      formatData[key] = value
    })
    .catch((err) => {
      if (err) console.error(err)
    })
}

function openAllPreviewDialog() {
  console.error(formatData)
  loadFormatData()
  visible.value = true
}

watch(visible, (open) => {
  if (!open) return
  setPreviewScale()
})

useEventListener(window, 'popstate', closeAllPreviewDialog)

onBeforeMount(() => {
  loadFormatData()
  emitter.on(OPEN_ALL_PREVIEW_DIALOG, openAllPreviewDialog)
})

onBeforeUnmount(() => {
  emitter.off(OPEN_ALL_PREVIEW_DIALOG, openAllPreviewDialog)
})
</script>

<style lang="scss" src="@/assets/scss/formats.scss"></style>
<style lang="scss" src="@/assets/scss/pages/pages.scss"></style>

<!-- content-class は vue-final-modal 内部ノードのため scoped 外 -->
<style>
.all-preview-dialog__container {
  z-index: 1001;
}

.all-preview-dialog__content.v--modal-box.v--modal {
  position: relative;
  box-sizing: border-box;
  width: calc(100% - 32px);
  max-width: 900px;
  height: auto;
  margin: 20px auto;
  padding: 16px;
  border-radius: 8px;
  background: #fff;
}
</style>
